import * as React from 'react';

/**
 * Icon set for `StatusIndicatorPill`. Same visual language as the Patient
 * Profile's Gut Checks card (`never-logged-in` / `onboarding` /
 * `baseline-complete`, extracted here so they stay in exactly one place) —
 * thin 1px stroke, 14x14 viewBox, `currentColor` so the pill's `fg` drives
 * both the icon and the label. The three additional icons follow the same
 * stroke language for the Care Coordination statuses that have no
 * `AppStatus` equivalent (booked/occurred visit, cancelled).
 */

type IconProps = { size?: number };

const common = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 14 14',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
});

/** Hollow circle — nothing started yet. */
export const NeverLoggedInIcon = ({ size = 12 }: IconProps) => (
  <svg {...common(size)}>
    <circle cx="7" cy="7" r="4.5" />
  </svg>
);

/** Pulse/activity line — in progress. */
export const OnboardingIcon = ({ size = 12 }: IconProps) => (
  <svg {...common(size)}>
    <path d="M1.5 7h2L5 3.5l2 7 1.5-4.5 1 1.5h1.5" />
  </svg>
);

/** Check inside a circle — a milestone reached, persists once earned. */
export const BaselineCompleteIcon = ({ size = 12 }: IconProps) => (
  <svg {...common(size)}>
    <path d="M5 7.25L6.5 8.75 9.25 5.5" />
    <circle cx="7" cy="7" r="5.25" />
  </svg>
);

/** Calendar — a visit is on the books but hasn't happened yet. */
export const ScheduledIcon = ({ size = 12 }: IconProps) => (
  <svg {...common(size)}>
    <rect x="2" y="3" width="10" height="9" rx="1.5" />
    <path d="M2 6h10M5 1.5v3M9 1.5v3" />
  </svg>
);

/** Plain checkmark — the visit itself happened (distinct from the milestone check-in-circle). */
export const VisitOccurredIcon = ({ size = 12 }: IconProps) => (
  <svg {...common(size)}>
    <path d="M2.5 7.25L5.25 10 11.5 3.5" />
  </svg>
);

/** Circular arrow — care that is underway and continuing, not a one-off milestone. */
export const OngoingCareIcon = ({ size = 12 }: IconProps) => (
  <svg {...common(size)}>
    <path d="M11.5 7a4.5 4.5 0 1 1-1.5-3.35" />
    <path d="M11.6 1.9v2.6H9" />
  </svg>
);

/** X — cancelled. */
export const CancelledIcon = ({ size = 12 }: IconProps) => (
  <svg {...common(size)}>
    <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" />
  </svg>
);
